import { Box, Typography, Alert, Button, IconButton, Tooltip } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as constantes from "../../../common/Constantes";
import PacientesUtils from "../../../utils/Pacientes";
import IPaciente from "../../../types/Paciente";
import { PacienteData } from "./DetalleFactura";
import ConfigUtils from "../../../utils/Config";
import EditIcon from "@mui/icons-material/Edit";

export interface CitaData {
  fecha: string;
  hora: string;
}

interface Props {
  esFacturable: boolean;
  estado?: string;
  paciente?: IPaciente | PacienteData;
  cita: CitaData;
  pacienteMenor?: string;
  emisor?: { razonSocial: string };
  onEditarEmisor?: () => void;
}

function esPacienteCompleto(
  estado: string | undefined,
  paciente: IPaciente | PacienteData
): paciente is IPaciente {
  return estado !== "Emitida";
}

const CabeceraFactura = ({ esFacturable, estado, paciente, cita, pacienteMenor, emisor, onEditarEmisor }: Props) => {
  const navigate = useNavigate();
  const [showMissingAlert, setShowMissingAlert] = useState(true);

  if (!paciente) return null;

  const pacienteCompleto = esPacienteCompleto(estado, paciente);

  const edad =
    pacienteCompleto && paciente.fechaNacimiento
      ? PacientesUtils.calcularEdad(paciente.fechaNacimiento)
      : null;

  const missingFields = pacienteCompleto
    ? [
        !paciente.domicilio ? "Dirección" : null,
        !paciente.fechaNacimiento ? "Fecha de Nacimiento" : null,
    
        (edad !== null && edad !== undefined) && edad < 18
          ? !paciente.DNItutor
            ? "DNI del tutor"
            : null
          : !paciente.DNI
            ? "DNI"
            : null,
      ].filter((f): f is string => Boolean(f))
    : [];

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#f8f9fa",
        border: "1px solid #e5e5e5",
      }}
    >
      {/* Aviso de datos incompletos */}
      {esFacturable && missingFields.length > 0 && showMissingAlert && (
        <Alert
          severity="warning"
          sx={{ mb: 1, fontSize: "0.875rem" }}
          onClose={() => setShowMissingAlert(false)}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">
              Faltan datos importantes: <b>{missingFields.join(", ")}</b>.
            </Typography>

            <Button
              size="small"
              color="warning"
              onClick={() => navigate(constantes.R_NUEVOPACIENTE, { state: paciente })}
            >
              Editar
            </Button>
          </Box>
        </Alert>
      )}

      {/* Emisor — solo si hay más de una entidad de facturación configurada, si no ya se sabe cuál es */}
      {emisor && ConfigUtils.getDatosFacturacionAdicional().length > 0 && (
        <Box display="flex" alignItems="center" gap={0.5} mb={1.5} pb={1} sx={{ borderBottom: "1px solid #e5e5e5" }}>
          <Typography variant="body2" color="text.secondary">
            <b>Entidad emisora:</b> {emisor.razonSocial}
          </Typography>
          {onEditarEmisor && (
            <Tooltip title="Cambiar entidad de facturación">
              <IconButton size="small" onClick={onEditarEmisor}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between">
        {/* Datos del receptor */}
        <Box flex={1}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Receptor
          </Typography>

          {pacienteCompleto ? (
            paciente.DNItutor ? (
              <>
                <Typography variant="body2">
                  <b>Paciente:</b> {paciente.nombre} {paciente.apellidos}
                </Typography>
                <Typography variant="body2">
                  <b>Nombre:</b> {paciente.nombreTutor} {paciente.apellidosTutor}
                </Typography>
                <Typography variant="body2">
                  <b>DNI:</b> {paciente.DNItutor}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2">
                  <b>Nombre:</b> {paciente.nombre} {paciente.apellidos}
                </Typography>            
                {paciente.DNI && (
                  <Typography variant="body2">
                    <b>DNI:</b> {paciente.DNI}
                  </Typography>
                )}          
                {paciente.email && (
                  <Typography variant="body2">
                    <b>Email:</b> {paciente.email}
                  </Typography>
                )}
              </>
            )
          ) : (
            <>
              {pacienteMenor && <Typography variant="body2">
                  <b>Paciente:</b> {pacienteMenor}
              </Typography>}
              <Typography variant="body2">
                <b>Nombre:</b> {paciente.nombre}
              </Typography>
              <Typography variant="body2">
                <b>DNI:</b> {paciente.nif}
              </Typography>
              {paciente.email && (
                <Typography variant="body2">
                  <b>Email:</b> {paciente.email}
                </Typography>
              )}
            </>
          )}

          {
            pacienteCompleto && paciente.domicilio && (
              <Typography variant="body2">
                <b>Domicilio:</b> {paciente.domicilio}
              </Typography>
            )
          }
          {
            !pacienteCompleto && paciente.direccion && (
              <Typography variant="body2">
                <b>Domicilio:</b> {paciente.direccion}
              </Typography>
            )
          }
        </Box>

        {/* Detalles de la cita */}
        {cita.fecha !== "" && cita.hora !== "" && <Box flexShrink={0}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Cita
          </Typography>
          <Typography variant="body2"><b>Fecha:</b> {cita.fecha}</Typography>
          <Typography variant="body2"><b>Hora:</b> {cita.hora}</Typography>
        </Box>}
      </Box>
    </Box>
  );
};

export default CabeceraFactura;
